import { useState } from 'react'
import { BookmarkIcon } from '@solar-icons/react/linear/bookmark'
import { BookmarkIcon as BookmarkFilledIcon } from '@solar-icons/react/bold/bookmark'
import { RefreshIcon } from '@solar-icons/react/linear/refresh'
import { MagicWandIcon } from '@solar-icons/react/linear/magic-wand'
import { DislikeIcon } from '@solar-icons/react/linear/dislike'
import { DislikeIcon as DislikeFilledIcon } from '@solar-icons/react/bold/dislike'
import { StopwatchIcon } from '@solar-icons/react/linear/stopwatch'
import { VolumeLoudIcon } from '@solar-icons/react/linear/volume-loud'
import { VolumeCrossIcon } from '@solar-icons/react/linear/volume-cross'
import type { Exchange } from '@/stores/sessionStore'
import { Badge, Button, CopyButton, PulseBars } from '@/components/ui'
import { AnswerText } from './AnswerText'
import { ExchangeError } from './ExchangeError'
import { SuggestionChips } from './SuggestionChips'
import { ResultTable } from '@/features/results/ResultTable'
import { ResultToolbar } from '@/features/results/ResultToolbar'
import { formatDuration } from '@/lib/format/cells'
import { cn } from '@/lib/utils/cn'
import { useSavedStore } from '@/stores/savedStore'

export interface ExchangeItemProps {
  exchange: Exchange
  onRerun: (query: string) => void
}

export function ExchangeItem({ exchange, onRerun }: ExchangeItemProps) {
  const saveQuery = useSavedStore((state) => state.save)
  const isSaved = useSavedStore((state) => state.isSaved(exchange.query))

  const [speaking, setSpeaking] = useState(false)
  const [disliked, setDisliked] = useState(false)

  const { outcome } = exchange
  const isUnresolved = !!outcome && outcome.status !== 'ok'

  const toggleSpeak = () => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window) || !outcome) return
    if (speaking) {
      window.speechSynthesis.cancel()
      setSpeaking(false)
      return
    }
    const utterance = new SpeechSynthesisUtterance(outcome.answer)
    utterance.onend = () => setSpeaking(false)
    utterance.onerror = () => setSpeaking(false)
    window.speechSynthesis.cancel()
    window.speechSynthesis.speak(utterance)
    setSpeaking(true)
  }

  const speechSupported = typeof window !== 'undefined' && 'speechSynthesis' in window

  return (
    <article className="animate-rise space-y-3">
      {/* Prompt bubble */}
      <div className="flex justify-end">
        <p className="max-w-[75%] rounded-3xl bg-accent px-4 py-2.5 text-sm leading-6 break-words text-accent-fg">
          {exchange.query}
        </p>
      </div>

      {/* Response bubble */}
      {exchange.status === 'pending' && (
        <div className="flex justify-start">
          <div className="flex items-center gap-2.5 rounded-3xl border border-line bg-surface px-4 py-3 font-mono text-xs text-muted shadow-sm">
            <PulseBars className="text-accent" />
            <span>Running the pipeline…</span>
            <span className="text-subtle">
              retrieval → SQL → validation → execution
            </span>
          </div>
        </div>
      )}

      {exchange.status === 'cancelled' && (
        <div className="flex justify-start">
          <div className="rounded-3xl border border-line bg-surface px-4 py-3 text-xs text-muted shadow-sm">
            Cancelled before the server responded.
          </div>
        </div>
      )}

      {exchange.status === 'error' && exchange.error && (
        <div className="flex justify-start">
          <div className="max-w-[85%]">
            <ExchangeError
              error={exchange.error}
              onRetry={() => onRerun(exchange.query)}
            />
          </div>
        </div>
      )}

      {/* Unresolved / needs-clarification / generic-failure outcome */}
      {exchange.status === 'success' && outcome && isUnresolved && (
        <div className="space-y-2">
          <div className="flex justify-start">
            <div className="max-w-[85%] rounded-3xl border border-line bg-surface px-4 py-3 shadow-sm">
              <SuggestionChips
                message={outcome.message}
                suggestions={outcome.suggestions ?? []}
                onSuggestionClick={(suggestion) => onRerun(suggestion)}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <Badge title="No SQL could be generated for this request">
              {outcome.status === 'needs_clarification' ? 'needs clarification' : 'unresolved'}
            </Badge>

            {typeof exchange.durationMs === 'number' && (
              <Badge icon={<StopwatchIcon size={11} strokeWidth={2} />}>
                {formatDuration(exchange.durationMs)}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onRerun(exchange.query)}
              aria-label="Ask again"
              title="Run this query again"
            >
              <RefreshIcon size={14} strokeWidth={1.75} />
            </Button>
          </div>
        </div>
      )}

      {/* Normal successful outcome */}
      {exchange.status === 'success' && outcome && !isUnresolved && (
        <div className="space-y-2">
          <div className="flex justify-start">
            <div className="max-w-[85%] rounded-3xl border border-line bg-surface px-4 py-3 shadow-sm">
              <AnswerText answer={outcome.answer} />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {outcome.analysis.usedLlm ? (
              <Badge tone="accent" icon={<MagicWandIcon size={11} strokeWidth={2} />}>
                LLM answer
              </Badge>
            ) : (
              <Badge title="Generated by the deterministic formatter in app/api/query.py">
                deterministic
              </Badge>
            )}

            {outcome.analysis.isConversational && (
              <Badge title="Resolved using context from the previous query">
                follow-up
              </Badge>
            )}

            {outcome.analysis.analysisMode &&
              outcome.analysis.analysisMode !== 'DETERMINISTIC' && (
                <Badge tone="accent">
                  {outcome.analysis.analysisMode.toLowerCase()}
                </Badge>
              )}

            {typeof outcome.analysis.investigations === 'number' &&
              outcome.analysis.investigations > 0 && (
                <Badge title="Follow-up queries the answer service ran to explain the result">
                  {outcome.analysis.investigations} investigation
                  {outcome.analysis.investigations === 1 ? '' : 's'}
                </Badge>
              )}

            {typeof exchange.durationMs === 'number' && (
              <Badge icon={<StopwatchIcon size={11} strokeWidth={2} />}>
                {formatDuration(exchange.durationMs)}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSpeak}
              disabled={!speechSupported}
              aria-label={speaking ? 'Stop reading aloud' : 'Read answer aloud'}
              title={speechSupported ? (speaking ? 'Stop reading aloud' : 'Read aloud') : 'Not supported in this browser'}
            >
              {speaking ? (
                <VolumeCrossIcon size={14} strokeWidth={1.75} />
              ) : (
                <VolumeLoudIcon size={14} strokeWidth={1.75} />
              )}
            </Button>

            <CopyButton value={outcome.answer} label="" copiedLabel="" size="icon" />

            <Button
              variant="ghost"
              size="icon"
              aria-label={isSaved ? 'Already saved' : 'Save this query'}
              title={isSaved ? 'Already in Saved' : 'Save this query'}
              disabled={isSaved}
              onClick={() => saveQuery({ query: exchange.query })}
            >
              {isSaved ? (
                <BookmarkFilledIcon size={14} />
              ) : (
                <BookmarkIcon size={14} strokeWidth={1.75} />
              )}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDisliked((value) => !value)}
              aria-label={disliked ? 'Remove not-helpful mark' : 'Mark not helpful'}
              title={disliked ? 'Remove not-helpful mark' : 'Mark not helpful'}
              className={cn(disliked && 'text-danger')}
            >
              {disliked ? (
                <DislikeFilledIcon size={14} />
              ) : (
                <DislikeIcon size={14} strokeWidth={1.75} />
              )}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => onRerun(exchange.query)}
              aria-label="Ask again"
              title="Run this query again"
            >
              <RefreshIcon size={14} strokeWidth={1.75} />
            </Button>
          </div>

          {outcome.result.columns.length > 0 && outcome.result.rows.length > 0 && (
            <div className="space-y-2 pt-1">
              <ResultToolbar result={outcome.result} query={exchange.query} />
              <ResultTable result={outcome.result} />
              {outcome.result.note && (
                <p className="px-1 text-[11px] text-warning">{outcome.result.note}</p>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  )
}